import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from '../components/BrandLogo';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { DIAL_CODES } from '../utils/dialCodes';
import { track } from '../utils/analytics';
import './EmailAuth.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const RESEND_SECONDS = 60;

const SignupPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const getRecaptchaToken = useRecaptcha();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dialCode, setDialCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      const recaptchaToken = await getRecaptchaToken('signup');
      const res = await fetch(`${API_BASE}/auth/email/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: `${dialCode}${phone.replace(/\D/g, '')}`,
          recaptchaToken,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      track('sign_up', { method: 'email' });
      setSentTo(email.trim());
      setCooldown(RESEND_SECONDS);
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  const ready = name.trim() && email.trim() && phone.replace(/\D/g, '').length >= 6;

  return (
    <div className="login-page-fullscreen">
      <Helmet>
        <title>Create account — ViralAI</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="login-container">
        <div className="login-header">
          <BrandLogo variant="onlight" height={48} className="login-logo" />
          <h1>Create your account</h1>
        </div>

        {sentTo ? (
          <div className="ea-sent" role="status">
            <h2>Check your inbox</h2>
            <p>
              We have sent a link to <strong>{sentTo}</strong>. Open it to finish creating your
              account. It works once and expires in 15 minutes.
            </p>
            <button type="button" className="ea-btn-link" disabled={sending || cooldown > 0} onClick={() => void submit()}>
              {cooldown > 0 ? `Resend link in ${cooldown}s` : 'Resend link'}
            </button>
            {error && <div className="ea-error" role="alert">{error}</div>}
            <button type="button" className="ea-btn-link" onClick={() => { setSentTo(null); setError(null); }}>
              Change details
            </button>
          </div>
        ) : (
          <form className="ea-form" onSubmit={onSubmit} noValidate>
            <div className="ea-field">
              <label htmlFor="signup-name">Full name</label>
              <input id="signup-name" className="ea-input" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="ea-field">
              <label htmlFor="signup-email">Email</label>
              <input id="signup-email" className="ea-input" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="ea-field">
              <label htmlFor="signup-phone">Phone</label>
              <div className="ea-phone">
                <select className="ea-select" aria-label="Country code" value={dialCode} onChange={(e) => setDialCode(e.target.value)}>
                  {DIAL_CODES.map((c) => <option key={c.label} value={c.code}>{c.label}</option>)}
                </select>
                <input id="signup-phone" className="ea-input" type="tel" inputMode="tel" autoComplete="tel-national" placeholder="98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} required />
              </div>
            </div>
            {error && <div className="ea-error" role="alert">{error}</div>}
            <button type="submit" className="ea-btn" disabled={sending || !ready}>
              {sending ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}

        <p className="ea-switch">
          Have an account? <Link to="/login">Sign in</Link>
        </p>

        <p className="login-terms">
          By proceeding, I agree to <a href="/terms">T&C</a> & <a href="/privacy">Privacy Policy</a>
        </p>
        <p className="login-terms">
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
